import { useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "react-responsive";
import "bulma/css/bulma.css";
import "../../../../../index.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { APISD } from "../../../../../config/apisd";
import { APIMS } from "../../../../../config/apims";
import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import { PulseLoader } from "react-spinners";
import ToastError from "../../../../NotificationToast/ToastError";

const normalizeSemesterChildren = (children, matpelId, fallbackColspan = 0) => {
  if (Array.isArray(children) && children.length > 0) {
    return children.map((child, index) => {
      const nameSemester =
        child?.name_semester ??
        child?.semester ??
        child?.title ??
        child?.label ??
        index + 1;

      return {
        semester_id:
          child?.semester_id ||
          child?.ysb_semester_id ||
          child?.id ||
          `${matpelId || "matpel"}-semester-${index}`,
        name_semester: nameSemester,
        label: `Semester ${nameSemester}`,
      };
    });
  }

  const totalColspan = Number(fallbackColspan || 0);

  if (totalColspan > 0) {
    return Array.from({ length: totalColspan }).map((_, index) => {
      const semesterNumber = index + 1;

      return {
        semester_id: `${matpelId || "matpel"}-semester-${semesterNumber}`,
        name_semester: semesterNumber,
        label: `Semester ${semesterNumber}`,
      };
    });
  }

  return [];
};

export default function MataPelajaran() {
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 780px)" });

  const [isFetching, setIsFetching] = useState(false);
  const [isFetchingBookScore, setIsFetchingBookScore] = useState(false);

  const [semesterOptions, setSemesterOptions] = useState([]);
  const [selectedSemesterId, setSelectedSemesterId] = useState("");
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState("");

  const [getData, setGetData] = useState([]);
  const [selectedTingkatan, setSelectedTingkatan] = useState("");
  const [selectedKelasId, setSelectedKelasId] = useState("");

  const [bookScoreData, setBookScoreData] = useState({
    header: [],
    informasi: {},
    data_murid: [],
  });

  const [bookScoreEmptyMessage, setBookScoreEmptyMessage] = useState(
    "Data nilai siswa belum tersedia"
  );

  useEffect(() => {
    document.title = "Transkip Nilai";
  }, []);

  const getToken = () => {
    return localStorage.getItem("token") || "";
  };

  const getLoginUser = () => {
    const keys = ["user", "user_data", "data_user", "auth_user"];

    for (const key of keys) {
      try {
        const raw = localStorage.getItem(key);
        if (raw) return JSON.parse(raw);
      } catch (error) {
        // skip kalau data localStorage bukan JSON valid
      }
    }

    return {};
  };

  const findValueDeep = (obj, keys = []) => {
    if (!obj || typeof obj !== "object") return "";

    for (const key of keys) {
      if (obj?.[key]) return obj[key];
    }

    for (const value of Object.values(obj)) {
      if (value && typeof value === "object") {
        const found = findValueDeep(value, keys);
        if (found) return found;
      }
    }

    return "";
  };

  const getTeacherId = () => {
    const user = getLoginUser();

    return (
      localStorage.getItem("teacherID") ||
      localStorage.getItem("id_teacher") ||
      localStorage.getItem("ysb_teacher_id") ||
      user?.teacherID ||
      user?.id_teacher ||
      user?.ysb_teacher_id ||
      user?.teacher_id ||
      user?.teacher?.id ||
      user?.teacher_data?.id ||
      user?.data?.teacherID ||
      user?.data?.id_teacher ||
      user?.data?.ysb_teacher_id ||
      user?.data?.teacher?.id ||
      ""
    );
  };

  const getBranchId = () => {
    const user = getLoginUser();

    const localValue =
      localStorage.getItem("ysb_branch_id") ||
      localStorage.getItem("branch_id") ||
      localStorage.getItem("branchID") ||
      localStorage.getItem("branch") ||
      localStorage.getItem("kode_branch") ||
      "";

    if (localValue) return localValue;

    return (
      findValueDeep(user, [
        "ysb_branch_id",
        "branch_id",
        "branchID",
        "branch",
        "kode_branch",
        "code_branch",
        "branch_code",
      ]) || ""
    );
  };

  const getSchoolId = () => {
    const user = getLoginUser();

    const localValue =
      localStorage.getItem("ysb_school_id") ||
      localStorage.getItem("school_id") ||
      localStorage.getItem("schoolID") ||
      localStorage.getItem("school") ||
      "";

    if (localValue) return localValue;

    return (
      findValueDeep(user, [
        "ysb_school_id",
        "school_id",
        "schoolID",
        "school",
        "id_school",
      ]) || ""
    );
  };

  const getSemesterArrayFromResponse = (responseData) => {
    if (Array.isArray(responseData?.data?.data)) return responseData.data.data;
    if (Array.isArray(responseData?.data)) return responseData.data;
    if (Array.isArray(responseData?.result)) return responseData.result;
    if (Array.isArray(responseData)) return responseData;

    return [];
  };

  const getMatpelArrayFromResponse = (responseData) => {
    if (Array.isArray(responseData?.data?.data_matpel)) {
      return responseData.data.data_matpel;
    }

    if (Array.isArray(responseData?.data?.data?.data_matpel)) {
      return responseData.data.data.data_matpel;
    }

    if (Array.isArray(responseData?.data?.ysb_matpel)) {
      return responseData.data.ysb_matpel;
    }

    if (Array.isArray(responseData?.data?.data?.ysb_matpel)) {
      return responseData.data.data.ysb_matpel;
    }

    if (Array.isArray(responseData?.data?.data)) return responseData.data.data;
    if (Array.isArray(responseData?.data)) return responseData.data;
    if (Array.isArray(responseData?.result)) return responseData.result;
    if (Array.isArray(responseData)) return responseData;

    return [];
  };

  const resetBookScoreData = (message = "Data nilai siswa belum tersedia") => {
    setBookScoreData({
      header: [],
      informasi: {},
      data_murid: [],
    });
    setBookScoreEmptyMessage(message);
  };

  const getSemesterData = async () => {
    setIsFetching(true);

    try {
      const response = await APIMS.get("/api/access/semesters", {
        headers: {
          Authorization: getToken(),
          "Content-Type": "application/json",
        },
        params: {
          page: 1,
          limit: 100,
          ascending: 0,
          search: "",
        },
      });

      const rows = getSemesterArrayFromResponse(response?.data);

      setSemesterOptions(rows);

      const activeSemester =
        rows.find((item) => Number(item?.status) === 1) || rows[0];

      if (activeSemester) {
        setSelectedSemesterId(activeSemester?.id || "");
        setSelectedAcademicYearId(activeSemester?.ysb_academic_year_id || "");
      }
    } catch (error) {
      ToastError.fire({
        icon: "error",
        title:
          error?.response?.data?.message || "Gagal mengambil data semester",
      });
    } finally {
      setIsFetching(false);
    }
  };

  const getMatpelTeacherData = async ({ semesterId, academicYearId }) => {
    if (!semesterId || !academicYearId) {
      setGetData([]);
      return;
    }

    setIsFetching(true);

    try {
      const params = {
        ysb_academic_year_id: academicYearId,
        ysb_semester_id: semesterId,
        ysb_branch_id: getBranchId(),
        ysb_school_id: getSchoolId(),
      };

      const teacherId = getTeacherId();

      if (teacherId) {
        params.ysb_teacher_id = teacherId;
      }

      const response = await APISD.request({
        method: "GET",
        url: "/api/matpel-assignments",
        headers: {
          Authorization: getToken(),
          "Content-Type": "application/json",
        },
        params,
      });

      const rows = getMatpelArrayFromResponse(response?.data);

      const normalizedRows = rows.flatMap((item, index) => {
        const rawKelasList = Array.isArray(item?.kelas_list)
          ? item.kelas_list
          : Array.isArray(item?.kelas)
          ? item.kelas
          : [];

        const sourceKelasList =
          rawKelasList.length > 0
            ? rawKelasList
            : item?.ysb_kelas_id
            ? [item]
            : [];

        const rawAssignments = Array.isArray(item?.assignments)
          ? item.assignments
          : [];

        const matpelId = item?.ysb_matpel_id || item?.id || `matpel-${index}`;

        const namaMatpel =
          item?.name_matpel ||
          item?.nama_matpel ||
          item?.matpel_name ||
          "-";

        const kelasList = sourceKelasList.map((kelas) => {
          const assignment = rawAssignments.find(
            (assign) => assign?.assignment_id === kelas?.assignment_id
          );

          return {
            assignment_id:
              kelas?.assignment_id || assignment?.assignment_id || "",

            ysb_kelas_id:
              kelas?.ysb_kelas_id ||
              kelas?.kelas_id ||
              assignment?.ysb_kelas_id ||
              "",

            ysb_grade_level_id:
              kelas?.ysb_grade_level_id ||
              kelas?.grade_level_id ||
              assignment?.ysb_grade_level_id ||
              "",

            ysb_branch_id:
              kelas?.ysb_branch_id ||
              assignment?.ysb_branch_id ||
              item?.ysb_branch_id ||
              getBranchId(),

            ysb_school_id:
              kelas?.ysb_school_id ||
              assignment?.ysb_school_id ||
              item?.ysb_school_id ||
              getSchoolId(),

            name_level:
              kelas?.name_level ||
              kelas?.["name level"] ||
              kelas?.level ||
              assignment?.name_level ||
              assignment?.["name level"] ||
              item?.name_level ||
              item?.["name level"] ||
              "",

            nama_kelas:
              kelas?.nama_kelas ||
              kelas?.name_kelas ||
              kelas?.kelas_name ||
              assignment?.name_kelas ||
              assignment?.nama_kelas ||
              item?.nama_kelas ||
              item?.name_kelas ||
              "-",
          };
        });

        return kelasList.map((kelas) => ({
          row_key: `${matpelId}-${kelas?.ysb_kelas_id || index}`,
          id: matpelId,
          ysb_matpel_id: matpelId,
          nama_matpel: namaMatpel,
          ...kelas,
        }));
      });

      setGetData(normalizedRows);
    } catch (error) {
      setGetData([]);

      ToastError.fire({
        icon: "error",
        title:
          error?.response?.data?.message ||
          "Gagal mengambil data tingkatan dan kelas",
      });
    } finally {
      setIsFetching(false);
    }
  };

  const nameCollator = useMemo(
    () => new Intl.Collator("id", { sensitivity: "base", numeric: true }),
    []
  );

  const allKelasOptions = useMemo(() => {
    const map = new Map();

    (getData || []).forEach((item) => {
      const kelasId = item?.ysb_kelas_id || "";

      if (!kelasId || map.has(kelasId)) return;

      map.set(kelasId, {
        ysb_kelas_id: kelasId,
        ysb_grade_level_id: item?.ysb_grade_level_id || "",
        ysb_branch_id: item?.ysb_branch_id || getBranchId(),
        ysb_school_id: item?.ysb_school_id || getSchoolId(),
        name_level: item?.name_level || "",
        nama_kelas: item?.nama_kelas || "-",
      });
    });

    return Array.from(map.values()).sort((a, b) => {
      const levelA = Number(a?.name_level);
      const levelB = Number(b?.name_level);

      if (!Number.isNaN(levelA) && !Number.isNaN(levelB)) {
        if (levelA !== levelB) return levelA - levelB;
      }

      return nameCollator.compare(
        (a?.nama_kelas || "").toString(),
        (b?.nama_kelas || "").toString()
      );
    });
  }, [getData, nameCollator]);

  const tingkatanOptions = useMemo(() => {
    const map = new Map();

    allKelasOptions.forEach((kelas) => {
      const levelKey = kelas?.name_level || "tanpa_tingkatan";

      if (!map.has(levelKey)) {
        map.set(levelKey, {
          value: levelKey,
          label:
            levelKey === "tanpa_tingkatan"
              ? "Tanpa Tingkatan"
              : `Kelas ${kelas?.name_level}`,
        });
      }
    });

    return Array.from(map.values()).sort((a, b) =>
      nameCollator.compare(a?.label || "", b?.label || "")
    );
  }, [allKelasOptions, nameCollator]);

  const kelasOptionsByTingkatan = useMemo(() => {
    if (!selectedTingkatan) return allKelasOptions;

    return allKelasOptions.filter((kelas) => {
      const levelKey = kelas?.name_level || "tanpa_tingkatan";
      return levelKey === selectedTingkatan;
    });
  }, [allKelasOptions, selectedTingkatan]);

  const selectedSemester = useMemo(() => {
    return semesterOptions.find(
      (item) => String(item?.id) === String(selectedSemesterId)
    );
  }, [semesterOptions, selectedSemesterId]);

  const selectedKelas = useMemo(() => {
    return allKelasOptions.find(
      (kelas) => String(kelas?.ysb_kelas_id) === String(selectedKelasId)
    );
  }, [allKelasOptions, selectedKelasId]);

  const academicYearOptions = useMemo(() => {
    const map = new Map();

    semesterOptions.forEach((item) => {
      const key = item?.ysb_academic_year_id || item?.id;
      if (!key) return;

      const oldItem = map.get(key);

      if (!oldItem || Number(item?.status) === 1) {
        map.set(key, item);
      }
    });

    return Array.from(map.values());
  }, [semesterOptions]);

  const selectedAcademicYearLabel = selectedSemester?.name_year || "2025-2026";
  const selectedAcademicYearDisplay = selectedAcademicYearLabel?.replace(
    "-",
    "/"
  );

  const matpelHeaders = useMemo(() => {
    const responseHeaders = Array.isArray(bookScoreData?.header)
      ? bookScoreData.header
      : [];

    const headersFromApi = responseHeaders
      .filter((item) => item?.matpel_id || item?.ysb_matpel_id)
      .map((item, index) => {
        const matpelId =
          item?.matpel_id ||
          item?.ysb_matpel_id ||
          item?.id ||
          `matpel-header-${index}`;

        const children = normalizeSemesterChildren(
          item?.children,
          matpelId,
          item?.colspan
        );

        return {
          matpel_id: matpelId,
          title:
            item?.title ||
            item?.name_matpel ||
            item?.nama_matpel ||
            `Matpel ${index + 1}`,
          children,
        };
      })
      .filter((item) => item.children.length > 0);

    if (headersFromApi.length > 0) return headersFromApi;

    const map = new Map();
    const dataMurid = Array.isArray(bookScoreData?.data_murid)
      ? bookScoreData.data_murid
      : [];

    dataMurid.forEach((murid) => {
      const dataNilai = Array.isArray(murid?.data_nilai)
        ? murid.data_nilai
        : [];

      dataNilai.forEach((nilaiMatpel, matpelIndex) => {
        const matpelId =
          nilaiMatpel?.matpel_id ||
          nilaiMatpel?.ysb_matpel_id ||
          nilaiMatpel?.id ||
          `matpel-data-${matpelIndex}`;

        if (!map.has(matpelId)) {
          map.set(matpelId, {
            matpel_id: matpelId,
            title:
              nilaiMatpel?.name_matpel ||
              nilaiMatpel?.nama_matpel ||
              nilaiMatpel?.title ||
              `Matpel ${matpelIndex + 1}`,
            children: [],
          });
        }

        const target = map.get(matpelId);
        const semesters = Array.isArray(nilaiMatpel?.semesters)
          ? nilaiMatpel.semesters
          : [];

        semesters.forEach((semester, semesterIndex) => {
          const semesterId =
            semester?.semester_id ||
            semester?.ysb_semester_id ||
            semester?.id ||
            `${matpelId}-semester-${semesterIndex}`;

          const isExists = target.children.some(
            (child) => String(child.semester_id) === String(semesterId)
          );

          if (!isExists) {
            const nameSemester =
              semester?.name_semester ?? semester?.semester ?? semesterIndex + 1;

            target.children.push({
              semester_id: semesterId,
              name_semester: nameSemester,
              label: `Semester ${nameSemester}`,
            });
          }
        });
      });
    });

    return Array.from(map.values()).filter((item) => item.children.length > 0);
  }, [bookScoreData]);

  const totalScoreColumns = useMemo(() => {
    return matpelHeaders.reduce((total, matpel) => {
      return total + (Array.isArray(matpel?.children) ? matpel.children.length : 0);
    }, 0);
  }, [matpelHeaders]);

  const sortedStudents = useMemo(() => {
    const students = Array.isArray(bookScoreData?.data_murid)
      ? bookScoreData.data_murid
      : [];

    return students
      .map((student, originalIndex) => ({ student, originalIndex }))
      .sort((a, b) => {
        const nameA = a.student?.nama_lengkap || a.student?.nama || "";
        const nameB = b.student?.nama_lengkap || b.student?.nama || "";
        const nameComparison = nameCollator.compare(nameA, nameB);

        return nameComparison !== 0
          ? nameComparison
          : a.originalIndex - b.originalIndex;
      })
      .map(({ student }) => student);
  }, [bookScoreData?.data_murid, nameCollator]);

  const getNilaiByMatpelSemester = (murid, matpel, semester) => {
    const dataNilai = Array.isArray(murid?.data_nilai) ? murid.data_nilai : [];

    const nilaiMatpel = dataNilai.find((item) => {
      const itemMatpelId = item?.matpel_id || item?.ysb_matpel_id || item?.id;
      return String(itemMatpelId) === String(matpel?.matpel_id);
    });

    if (!nilaiMatpel) return "-";

    const semesters = Array.isArray(nilaiMatpel?.semesters)
      ? nilaiMatpel.semesters
      : [];

    const nilaiSemester = semesters.find((item) => {
      const itemSemesterId =
        item?.semester_id || item?.ysb_semester_id || item?.id;

      const sameSemesterId =
        itemSemesterId &&
        semester?.semester_id &&
        String(itemSemesterId) === String(semester.semester_id);

      const sameSemesterName =
        item?.name_semester !== undefined &&
        semester?.name_semester !== undefined &&
        String(item.name_semester) === String(semester.name_semester);

      return sameSemesterId || sameSemesterName;
    });

    const nilaiRapor = nilaiSemester?.nilai_rapor;

    if (nilaiRapor === 0 || nilaiRapor === "0" || nilaiRapor) {
      return nilaiRapor;
    }

    return "-";
  };

  const getBookExamScoreRecaps = async () => {
    if (!selectedAcademicYearId || !selectedKelasId) {
      resetBookScoreData("Silakan pilih tahun ajaran, tingkatan, dan kelas");
      return;
    }

    const branchId = selectedKelas?.ysb_branch_id || getBranchId();
    const schoolId = selectedKelas?.ysb_school_id || getSchoolId();

    if (!branchId) {
      resetBookScoreData("Branch tidak ditemukan. Silakan login ulang.");

      ToastError.fire({
        icon: "error",
        title: "Branch tidak ditemukan. Silakan login ulang.",
      });

      return;
    }

    if (!schoolId) {
      resetBookScoreData("School tidak ditemukan. Silakan login ulang.");

      ToastError.fire({
        icon: "error",
        title: "School tidak ditemukan. Silakan login ulang.",
      });

      return;
    }

    setIsFetchingBookScore(true);

    try {
      const requestPayload = {
        ysb_academic_year_id: selectedAcademicYearId,
        ysb_kelas_id: selectedKelasId,
        ysb_branch_id: branchId,
        ysb_school_id: schoolId,
      };

      console.log("payload book exam score recaps:", requestPayload);

      const response = await APISD.request({
        method: "GET",
        url: "/api/book-exam-score-recaps",
        headers: {
          Authorization: getToken(),
          "Content-Type": "application/json",
        },
        params: requestPayload,
      });

      const payload = response?.data?.data || {};

      setBookScoreData({
        header: Array.isArray(payload?.header) ? payload.header : [],
        informasi: payload?.informasi || {},
        data_murid: Array.isArray(payload?.data_murid)
          ? payload.data_murid
          : [],
      });

      setBookScoreEmptyMessage("Data nilai siswa belum tersedia");
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        "";

      resetBookScoreData("Data nilai siswa belum tersedia");

      ToastError.fire({
        icon: "error",
        title: errorMessage || "Gagal mengambil data transkip nilai",
      });
    } finally {
      setIsFetchingBookScore(false);
    }
  };

  const handleChangeSemester = (e) => {
    const semesterId = e.target.value;
    const semester = semesterOptions.find(
      (item) => String(item?.id) === String(semesterId)
    );

    setSelectedSemesterId(semesterId);
    setSelectedAcademicYearId(semester?.ysb_academic_year_id || "");
    setSelectedTingkatan("");
    setSelectedKelasId("");
    resetBookScoreData();
  };

  const handleChangeTingkatan = (e) => {
    setSelectedTingkatan(e.target.value);
    setSelectedKelasId("");
    resetBookScoreData();
  };

  const handleChangeKelas = (e) => {
    setSelectedKelasId(e.target.value);
  };

  useEffect(() => {
    getSemesterData();
  }, []);

  useEffect(() => {
    if (selectedSemesterId && selectedAcademicYearId) {
      getMatpelTeacherData({
        semesterId: selectedSemesterId,
        academicYearId: selectedAcademicYearId,
      });
    }
  }, [selectedSemesterId, selectedAcademicYearId]);

  useEffect(() => {
    if (tingkatanOptions.length > 0 && !selectedTingkatan) {
      setSelectedTingkatan(tingkatanOptions[0]?.value || "");
    }
  }, [tingkatanOptions, selectedTingkatan]);

  useEffect(() => {
    if (kelasOptionsByTingkatan.length > 0 && !selectedKelasId) {
      setSelectedKelasId(kelasOptionsByTingkatan[0]?.ysb_kelas_id || "");
    }
  }, [kelasOptionsByTingkatan, selectedKelasId]);

  useEffect(() => {
    if (selectedAcademicYearId && selectedKelasId) {
      getBookExamScoreRecaps();
    }
  }, [selectedAcademicYearId, selectedKelasId]);

  const filterLabelStyle = {
    display: "block",
    marginBottom: 6,
    color: "#334155",
    fontSize: 13,
    fontWeight: 700,
  };

  const filterSelectStyle = {
    width: "100%",
    height: 42,
    borderRadius: 10,
    border: "1px solid #dbe4ee",
    padding: "0 12px",
    color: "#334155",
    fontSize: 13,
    outline: "none",
    backgroundColor: "#ffffff",
  };

  return (
    <div className="body">
      <style>{`
        .transkip-table-wrap {
          width: 100%;
          max-height: calc(100vh - 250px);
          overflow: auto;
          border: 1px solid #dbe4ee;
          border-radius: 10px;
          background: #ffffff;
          scrollbar-color: #cbd5e1 transparent;
          scrollbar-width: thin;
          -webkit-overflow-scrolling: touch;
        }

        .transkip-table-wrap::-webkit-scrollbar {
          width: 9px;
          height: 9px;
        }

        .transkip-table-wrap::-webkit-scrollbar-track {
          background: transparent;
        }

        .transkip-table-wrap::-webkit-scrollbar-thumb {
          border-radius: 999px;
          background: #cbd5e1;
        }

        .transkip-table {
          width: max-content !important;
          min-width: 100%;
          margin-bottom: 0 !important;
          border-collapse: separate !important;
          border-spacing: 0 !important;
          color: #334155;
          font-size: 13px;
        }

        .transkip-table th,
        .transkip-table td {
          height: 58px;
          padding: 12px 14px !important;
          border: 0 !important;
          border-right: 1px solid #e2e8f0 !important;
          border-bottom: 1px solid #e2e8f0 !important;
          text-align: center;
          vertical-align: middle !important;
          white-space: nowrap;
        }

        .transkip-table thead th {
          position: sticky;
          top: 0;
          z-index: 6;
          height: 58px;
          background: #f2f3fc !important;
          color: #172554 !important;
          font-weight: 700;
          white-space: normal;
        }

        .transkip-table thead tr:nth-child(2) th {
          top: 58px;
          z-index: 7;
        }

        .transkip-table tbody tr:nth-child(odd) td {
          background: #ffffff !important;
        }

        .transkip-table tbody tr:nth-child(even) td {
          background: #f8fafc !important;
        }

        .transkip-table tbody tr:hover td {
          background: #f1f5ff !important;
        }

        .transkip-table .transkip-no-column {
          position: sticky;
          left: 0;
          z-index: 3;
          width: 70px;
          min-width: 70px;
          max-width: 70px;
          padding-right: 8px !important;
          padding-left: 8px !important;
        }

        .transkip-table thead .transkip-no-column {
          z-index: 10;
          background: #f2f3fc !important;
        }

        .transkip-table .transkip-student-column {
          position: sticky;
          left: 70px;
          z-index: 3;
          width: 300px;
          min-width: 300px;
          max-width: 300px;
          text-align: left;
          white-space: normal;
           box-shadow: 5px 0 10px -10px rgba(0, 76, 255, 0.55);
        }

        .transkip-table thead .transkip-student-column {
          z-index: 10;
          background: #f2f3fc !important;
        }

        .transkip-table tbody .transkip-student-column {
          color: #1f2937;
          font-weight: 600;
        }

        .transkip-number {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border: 1px solid #d4ddff;
          border-radius: 50%;
          background: #f5f7ff;
          color: #3f5bea;
          font-weight: 700;
        }

        .transkip-score-column {
          width: 110px;
          min-width: 110px;
          color: #00a86b;
          font-weight: 700;
        }

        @media (max-width: 780px) {
          .transkip-table-wrap {
            max-height: calc(100vh - 210px);
          }

          .transkip-table .transkip-student-column {
            width: 220px;
            min-width: 220px;
            max-width: 220px;
          }
        }
      `}</style>
      <div className="page-header-card">
        <div className="page-header-top d-flex">
          <div className="page-title-wrap">
            <span className="page-title-icon">
              <FontAwesomeIcon icon={faCalendar} />
            </span>

            <div>
              <h4 className="page-title-text mb-0">Transkip Nilai</h4>
            </div>
          </div>
        </div>
      </div>

      <div className="body-content">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isTabletOrMobile
              ? "1fr"
              : "repeat(3, minmax(0, 1fr))",
            gap: 12,
            marginBottom: 18,
          }}
        >
          <div>
            <label style={filterLabelStyle}>Tahun Ajaran</label>
            <select
              value={selectedSemesterId}
              onChange={handleChangeSemester}
              style={filterSelectStyle}
              disabled={academicYearOptions.length === 0}
            >
              <option value="">Pilih Tahun Ajaran</option>
              {academicYearOptions.map((item) => (
                <option key={item?.id} value={item?.id}>
                  {(item?.name_year || selectedAcademicYearDisplay || "-").replace(
                    "-",
                    "/"
                  )}
                </option>
              ))}
            </select>
          </div>
        </div>

        {isFetching || isFetchingBookScore ? (
          <div
            className="text-center"
            style={{
              padding: "55px 0",
            }}
          >
            <PulseLoader color="#3f5bea" size={10} />

            <div
              style={{
                marginTop: 10,
                color: "#3f5bea",
                fontSize: 15,
              }}
            >
              Memuat data transkip nilai...
            </div>
          </div>
        ) : sortedStudents.length > 0 ? (
          <div className="transkip-table-wrap">
            <table
              className="table table-hover transkip-table"
              id="basic-datatable"
              style={{
                minWidth:
                  70 + 300 + Math.max(totalScoreColumns, 1) * 110,
              }}
            >
              <thead>
                <tr>
                  <th rowSpan={2} className="transkip-no-column text-center">
                    No
                  </th>

                  <th rowSpan={2} className="transkip-student-column text-center">
                    Siswa
                  </th>

                  {matpelHeaders.map((matpel) => (
                    <th
                      key={matpel.matpel_id}
                      colSpan={matpel.children.length}
                      style={{ minWidth: matpel.children.length * 110 }}
                      className="text-center"
                    >
                      {matpel.title}
                    </th>
                  ))}
                </tr>

                <tr>
                  {matpelHeaders.flatMap((matpel) =>
                    matpel.children.map((semester) => (
                      <th
                        key={`${matpel.matpel_id}-${semester.semester_id}`}
                        className="transkip-score-column text-center"
                      >
                        {semester.label}
                      </th>
                    ))
                  )}
                </tr>
              </thead>

              <tbody>
                {sortedStudents.map((murid, index) => (
                  <tr key={murid?.ysb_student_id || index}>
                    <td className="transkip-no-column text-center">
                      <span className="transkip-number ">{index + 1}</span>
                    </td>

                    <td className="transkip-student-column">
                      {murid?.nama_lengkap || murid?.nama || "-"}
                    </td>

                    {matpelHeaders.flatMap((matpel) =>
                      matpel.children.map((semester) => {
                        const nilai = getNilaiByMatpelSemester(
                          murid,
                          matpel,
                          semester
                        );

                        return (
                          <td
                            key={`${
                              murid?.ysb_student_id || index
                            }-${matpel.matpel_id}-${semester.semester_id}`}
                            className="transkip-score-column text-center"
                            style={{
                              color: nilai === "-" ? "#94a3b8" : undefined,
                              fontWeight: nilai === "-" ? 500 : 700,
                            }}
                          >
                            {nilai}
                          </td>
                        );
                      })
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div
            className="text-center"
            style={{
              padding: "45px 20px",
              border: "1px dashed #cbd5e1",
              borderRadius: 12,
              color: "#64748b",
              fontWeight: 600,
              backgroundColor: "#ffffff",
            }}
          >
            {bookScoreEmptyMessage}
          </div>
        )}
      </div>
    </div>
  );
}
